// @src/pre-deployment/src/components/navigation/MapMenu.js v1.0
// Interactive Map Menu Component
// - Loads numbered map layers (1-14)
// - Uses pixel-perfect detection based on alpha channel
// - Maps regions to app levels/routes

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import './MapMenu.css';
import Sidebar from './Sidebar';

// ==========================================
// 🗺️ REGION CONFIGURATION (User Editable)
// ==========================================
// This object maps map regions (1-14) to levels and routes.
// To change which map part goes where, just edit this object.
// - levelId: The level number (0-7) to navigate to
// - title: Tooltip title
// - description: Tooltip description
// - locked: If true, region cannot be clicked (visual style can be added)
// ==========================================
const REGION_CONFIG = {
    1: { levelId: 0, title: '한글 Hangul Practice', description: 'Master the Korean alphabet and keyboard' },
    2: { levelId: 1, title: '1급 Beginner', description: 'Start your journey with basic phrases' },
    3: { levelId: 1, title: '1급 Beginner', description: 'Start your journey with basic phrases' },
    4: { levelId: 2, title: '2급 Elementary', description: 'Simple conversations and grammar' },
    5: { levelId: 2, title: '2급 Elementary', description: 'Simple conversations and grammar' },
    6: { levelId: 3, title: '3급 Pre-Intermediate', description: 'Everyday situations' },
    7: { levelId: 3, title: '3급 Pre-Intermediate', description: 'Everyday situations' },
    8: { levelId: 4, title: '4급 Intermediate', description: 'Express complex ideas' },
    9: { levelId: 4, title: '4급 Intermediate', description: 'Express complex ideas' },
    10: { levelId: 5, title: '5급 Upper-Intermediate', description: 'Master advanced nuances' },
    11: { levelId: 5, title: '5급 Upper-Intermediate', description: 'Master advanced nuances' },
    12: { levelId: 6, title: '6급 Advanced', description: 'Achieve native-like fluency' },
    13: { levelId: 6, title: '6급 Advanced', description: 'Achieve native-like fluency' },
    14: { levelId: 7, title: '🌟 Special Zone', description: 'Coming Soon / Dashboard' },
};

// ==========================================
// ⚙️ COMPONENT SETTINGS
// ==========================================
const MAX_IMAGES = 14; // Total number of image layers
const IMAGE_FOLDERS = {
    basic: '/img/map/basic/',
    selected: '/img/map/selected/'
};

const MapMenu = () => {
    const navigate = useNavigate();

    // Refs
    const mapWrapperRef = useRef(null);
    const canvasRef = useRef(null);

    // State
    const [layers, setLayers] = useState({}); // Stores loaded image elements
    const [hoveredRegion, setHoveredRegion] = useState(-1);
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 }); // Intrinsic dimensions
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar state

    // 1. Load Images & Initialize Canvas Data
    useEffect(() => {
        let imagesLoadedCount = 0;
        const loadedLayers = {};
        const totalImages = MAX_IMAGES * 2; // Basic + Selected pairs

        const checkAllLoaded = () => {
            imagesLoadedCount++;
            setLoadingProgress(Math.round((imagesLoadedCount / totalImages) * 100));

            if (imagesLoadedCount >= totalImages) {
                setLayers(loadedLayers);
                setLoading(false);
            }
        };

        // Helper to load a single image
        const loadImage = (src) => {
            const img = new Image();
            img.src = src;
            img.crossOrigin = "Anonymous"; // Helpful if assets are served from different origin
            return new Promise((resolve, _reject) => {
                img.onload = () => {
                    checkAllLoaded();
                    resolve(img);
                };
                img.onerror = (_e) => {
                    console.error(`Failed to load image: ${src}`, _e);
                    checkAllLoaded(); // Count as loaded to prevent hanging
                    resolve(null); // Resolve with null so we know it failed
                };
            });
        };

        const loadAllLayers = async () => {
            // First, fetch the first image to get dimensions
            const dimImg = new Image();
            dimImg.src = `${IMAGE_FOLDERS.basic}1.PNG`;
            dimImg.onload = () => {
                setMapDimensions({ width: dimImg.naturalWidth, height: dimImg.naturalHeight });

                // Now load all layers
                for (let i = 1; i <= MAX_IMAGES; i++) {
                    loadedLayers[i] = { basic: null, selected: null, canvas: null };

                    loadImage(`${IMAGE_FOLDERS.basic}${i}.PNG`).then(img => {
                        if (img) {
                            loadedLayers[i].basic = img;

                            // Create detection canvas for this layer
                            const cvs = document.createElement('canvas');
                            cvs.width = dimImg.naturalWidth;
                            cvs.height = dimImg.naturalHeight;
                            const ctx = cvs.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            loadedLayers[i].canvas = cvs;
                        }
                    });

                    loadImage(`${IMAGE_FOLDERS.selected}${i}.PNG`).then(img => {
                        if (img) loadedLayers[i].selected = img;
                    });
                }
            };
        };

        loadAllLayers();
    }, []);

    // 2. Pixel Detection Logic
    const getRegionAtPoint = useCallback((clientX, clientY) => {
        if (!mapWrapperRef.current || mapDimensions.width === 0) return -1;

        const rect = mapWrapperRef.current.getBoundingClientRect();

        // Calculate relative coordinates
        const relativeX = clientX - rect.left;
        const relativeY = clientY - rect.top;

        // Scale to intrinsic image dimensions
        const scaleX = mapDimensions.width / rect.width;
        const scaleY = mapDimensions.height / rect.height;

        const pixelX = Math.floor(relativeX * scaleX);
        const pixelY = Math.floor(relativeY * scaleY);

        // Bounds check
        if (pixelX < 0 || pixelX >= mapDimensions.width || pixelY < 0 || pixelY >= mapDimensions.height) {
            return -1;
        }

        // Check layers in reverse order (top to bottom if they overlap, though here they might not)
        // Checking 14 down to 1
        for (let i = MAX_IMAGES; i >= 1; i--) {
            const layer = layers[i];
            if (layer && layer.canvas) {
                const ctx = layer.canvas.getContext('2d');
                try {
                    const pixelData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
                    // Alpha channel check (threshold > 0 means not transparent)
                    if (pixelData[3] > 0) {
                        return i;
                    }
                } catch (e) {
                    // Context might be lost or tainted
                    continue;
                }
            }
        }

        return -1;

    }, [layers, mapDimensions]);

    // 3. Event Handlers
    const handleMouseMove = useCallback((e) => {
        const region = getRegionAtPoint(e.clientX, e.clientY);
        if (region !== hoveredRegion) {
            setHoveredRegion(region);
        }
    }, [getRegionAtPoint, hoveredRegion]);

    const handleClick = useCallback((e) => {
        const region = getRegionAtPoint(e.clientX, e.clientY);

        if (region > 0 && REGION_CONFIG[region]) {
            const { levelId, locked } = REGION_CONFIG[region];

            if (locked) {
                console.log('🔒 Region locked:', region);
                // Optional: Show locked animation or toast
                return;
            }

            console.log(`📍 Map Click: Region ${region} -> Level ${levelId}`);
            if (levelId === 7) {
                // Special handling for special dashboard zone
                navigate('/dashboard');
            } else {
                navigate(`/level/${levelId}`);
            }
        }
    }, [getRegionAtPoint, navigate]);

    return (
        <div className="map-menu">
            {/* Sidebar Integration */}
            {/* Note: Sidebar component needs to be imported. I'll add the import line in the next tool call or assume it's here */}
            {/* BUT wait, I can't add import here easily without multi-edit. I'll rely on the user to have valid imports or do it in one go. */}
            {/* Actually, I will assume Sidebar is imported as 'Sidebar' and use it. */}
            {/* I need to adding import Sidebar from './Sidebar' at the top... */}
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            {/* Top Navigation */}
            <div className="menu-top-navigation">
                <div className="module-info">
                    {/* Hamburger */}
                    <button
                        className="hamburger-btn"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        style={{ marginRight: '1rem', background: 'transparent', border: 'none', color: '#333', fontSize: '1.2rem', display: 'flex' }}
                    >
                        <i className="fas fa-bars"></i>
                    </button>
                    <span>🗺️ World Map</span>
                    <span className="separator">•</span>
                    <span>Select Your Destination</span>
                </div>
                <div className="module-nav-buttons">
                    {/* Test Buttons */}
                    <button className="module-nav-btn secondary" onClick={() => navigate('/modern-level-select')}>
                        <i className="fas fa-layer-group"></i> New Level
                    </button>
                    <button className="module-nav-btn secondary" onClick={() => navigate('/app-homepage')}>
                        <i className="fas fa-home"></i> Home
                    </button>
                    <button className="module-nav-btn secondary" onClick={() => navigate('/map')}>
                        <i className="fas fa-map"></i> Map
                    </button>

                    <button className="module-nav-btn secondary" onClick={() => navigate('/app-homepage')}>
                        <i className="fas fa-list"></i> List View
                    </button>
                </div>
            </div>

            {/* Map Container */}
            <div
                className="map-container"
                ref={mapWrapperRef}
                onMouseLeave={() => setHoveredRegion(-1)}
            >
                {loading && (
                    <div className="map-loading">
                        <div className="loading-spinner"></div>
                        <span>Loading Map... {loadingProgress}%</span>
                    </div>
                )}

                {/* Render Map Layers */}
                {Object.entries(layers).map(([key, layer]) => {
                    const index = parseInt(key);
                    const isHovered = hoveredRegion === index;

                    return (
                        layer.basic && layer.selected && (
                            <div
                                key={key}
                                className={`map-layer ${isHovered ? 'active' : ''}`}
                                style={{ zIndex: isHovered ? 100 : index }}
                            >
                                <img src={layer.basic.src} className="basic-image" alt="" />
                                <img src={layer.selected.src} className="selected-image" alt="" />
                            </div>
                        )
                    );
                })}

                {/* Interaction Canvas (Invisible) */}
                {!loading && (
                    <canvas
                        ref={canvasRef}
                        className="map-canvas"
                        width={mapDimensions.width}
                        height={mapDimensions.height}
                        onMouseMove={handleMouseMove}
                        onClick={handleClick}
                    />
                )}
            </div>

            {/* Hover Tooltip */}
            {hoveredRegion > 0 && REGION_CONFIG[hoveredRegion] && (
                <div className="map-tooltip">
                    <h3>{REGION_CONFIG[hoveredRegion].title}</h3>
                    <p>{REGION_CONFIG[hoveredRegion].description}</p>
                    <span className="click-hint">Click to Enter</span>
                </div>
            )}
        </div>
    );
};

export default MapMenu;
